import { Card as CardModel, Card, HexColor } from "@/types/v2";
import React, { useMemo } from "react";
import { firestoreToDateTime, isPast } from "@/lib/utils";
import { serverNow } from "@/lib/serverClock";
import {
  COLOR_PICKER_COLORS,
  DEFAULT_CARD_COLOR,
  DEFAULT_TIMER_VALUE,
  PROMISE_TIME_PAST_COLOR,
} from "@/lib/const";

export interface CardAnimationsProps {
  cardData: Card | undefined;
  isPromiseTimeEnabled: boolean;
  isCardColorSyncEnabled: boolean;
  isCardColorEnabled: boolean;
  isDragging: boolean;
  startTime: number | null | "stop";
  promiseTimePastDueColor?: HexColor;
}
export const useCardAnimations = ({
  cardData,
  isCardColorSyncEnabled,
  isCardColorEnabled,
  isPromiseTimeEnabled,
  isDragging,
  startTime,
  promiseTimePastDueColor,
}: CardAnimationsProps) => {
  const pastDueColor = promiseTimePastDueColor ?? PROMISE_TIME_PAST_COLOR;

  const cardBackgroundColor = React.useMemo(() => {
    if (isPromiseTimeEnabled && isPast(cardData?.promisedTime)) {
      return pastDueColor;
    } else if (isCardColorSyncEnabled && cardData?.backgroundColor) {
      return cardData.backgroundColor;
    } else if (isCardColorEnabled && cardData?.color) {
      return cardData.color;
    } else if (isCardColorEnabled && cardData?.bgColor) {
      // TODO: REMOVE EVENTUALLY
      return cardData.bgColor;
    }

    return DEFAULT_CARD_COLOR;
  }, [
    cardData,
    isPromiseTimeEnabled,
    isCardColorSyncEnabled,
    isCardColorEnabled,
    pastDueColor,
  ]);

  const getBackgroundColor = (excludeAutomation: boolean = false): HexColor => {
    const theColor = !isCardColorEnabled
      ? startTime !== null && startTime !== "stop"
        ? Math.max(0, serverNow() - startTime) >
          parseInt(cardData?.timer?.timeLimitInMinutes ?? DEFAULT_TIMER_VALUE) *
            60 *
            1000
          ? "#FF0000"
          : "#FFFF00"
        : cardBackgroundColor
      : cardBackgroundColor;

    if (
      automationActionActive() &&
      automationAction("color") &&
      cardData?.automationActionData &&
      typeof cardData?.automationActionData === "string" &&
      !excludeAutomation
    ) {
      return cardData?.automationActionData.includes("#")
        ? (cardData?.automationActionData as HexColor)
        : `#${cardData?.automationActionData}`;
    } else if (isPromiseTimeEnabled && isPast(cardData?.promisedTime)) {
      return pastDueColor;
    } else if (isCardColorSyncEnabled && cardData?.backgroundColor) {
      return cardData.backgroundColor.includes("#")
        ? (cardData.backgroundColor as HexColor)
        : `#${cardData.backgroundColor}`;
    } else if (isCardColorEnabled && cardData?.color) {
      return cardData.color.includes("#")
        ? (cardData.color as HexColor)
        : `#${cardData.color}`;
    }
    return theColor.includes("#") ? (theColor as HexColor) : `#${theColor}`;
  };

  const automationActionActive = () => {
    const now = new Date().getTime();
    let startsAt = 0;
    if (cardData?.automationActionStartAt) {
      startsAt =
        firestoreToDateTime(cardData?.automationActionStartAt)?.getTime() ?? 0;
    }
    if (cardData?.automationActionExpireAt) {
      const expiresAt = firestoreToDateTime(
        cardData?.automationActionExpireAt
      )?.getTime();

      if (expiresAt && expiresAt <= now) {
        // The action has expired, return false
        return false;
      }
    }

    return !(startsAt && startsAt > now);
  };

  const manualActionActive = () => !!cardData?.manualAction;

  /**
   * Whichever effect started more recently governs rendering when both a
   * manual and an active automation effect are present — this implements
   * the pitch's decided collision rule ("whichever starts later takes
   * precedence"). If only one side is active, it governs unchanged.
   */
  const manualWins = () => {
    if (!manualActionActive()) return false;
    if (!automationActionActive() || !cardData?.automationAction) return true;

    const manualStartsAt =
      firestoreToDateTime(cardData?.manualActionStartAt)?.getTime() ?? 0;
    const automationStartsAt =
      firestoreToDateTime(cardData?.automationActionStartAt)?.getTime() ?? 0;

    return manualStartsAt > automationStartsAt;
  };

  const automationAction = (
    action: CardModel["automationAction"]
  ): null | boolean => {
    if (manualWins()) {
      switch (action) {
        case "wiggle":
          return !isDragging && cardData?.manualAction === "wiggle";
        case "color":
          return false;
        default:
          return cardData?.manualAction === action;
      }
    }

    if (!automationActionActive() || !cardData || !cardData?.automationAction) {
      return null;
    }

    switch (action) {
      case "wiggle":
        if (
          !isDragging &&
          ["wiggle", "colorAndWiggle"].includes(cardData.automationAction)
        ) {
          return true;
        }
        break;
      case "color":
        if (["color", "colorAndWiggle"].includes(cardData.automationAction)) {
          return true;
        }
        break;
      default:
        return cardData.automationAction === action;
    }

    return null;
  };

  // Computed fresh on every render (cheap, not memoized) so it always
  // reflects the current wall-clock state of automation expiry — the
  // `colors`/`animationName` memos below depend on this boolean directly
  // rather than re-deriving the winner from the raw automation/manual
  // fields themselves. Those fields can go a render or more without
  // changing (e.g. a re-render triggered by `isDragging`/zoom rather than
  // a Firestore write) while `automationActionExpireAt` silently lapses in
  // the background; keying the memos off the raw fields alone would leave
  // them caching the pre-expiry winner's color after the gate has already
  // flipped to the other side.
  const manualIsWinning = manualWins();

  const colors = useMemo(() => {
    const bgColor = getBackgroundColor(true);
    const activeFlashAction = manualIsWinning
      ? cardData?.manualAction === "flash"
      : cardData?.automationAction === "flash";
    const activeFlashData = manualIsWinning
      ? cardData?.manualActionData
      : cardData?.automationActionData;

    if (activeFlashAction) {
      if (activeFlashData && typeof activeFlashData === "string") {
        return [...activeFlashData.split(","), bgColor];
      }
      return [bgColor];
    }
    return [];
  }, [
    manualIsWinning,
    cardData?.automationAction,
    cardData?.automationActionData,
    cardData?.manualAction,
    cardData?.manualActionData,
    getBackgroundColor,
  ]);

  const animationName = useMemo(() => {
    const activeData = manualIsWinning
      ? cardData?.manualActionData
      : cardData?.automationActionData;
    const animationColor = COLOR_PICKER_COLORS.find(
      (color) => color.hex === activeData
    );
    if (animationColor) {
      return animationColor.label.replace("cp", "flash");
    }
  }, [
    manualIsWinning,
    cardData?.automationActionData,
    cardData?.manualActionData,
  ]);

  return {
    colors,
    animationName,
    getBackgroundColor,
    automationAction,
  };
};
